import { PPTLookupService } from '@/app/modules/ppt/services/ppt-lookup.service';
import { selectPPTAutoDraftEnabled, selectPPTStateLoaded, selectPPTSubscriptionsEnabled, selectPPTWatchListsEnabled } from '@/app/modules/ppt/state/ppt-state/ppt-state.selectors';
import { selectUserAccountsEnabled } from '@/app/state/app-state/app-state.selectors';
import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, Inject, Input, OnInit, Output } from '@angular/core';
import { AutoDraftService } from '@auto-draft/auto-draft.service';
import { sync } from '@ces/sync';
import { Platform } from '@egovsolutions/angular-platform';
import { selectLookupServiceAvailable } from '@lookup/state/lookup-state/lookup-state.selectors';
import { Store } from '@ngrx/store';
import { PPTCart } from '@ppt/services/ppt-cart.service';
import { selectPPTAccount, selectPPTAccountAddAllButtonEnabled, selectPPTAccountAddAllButtonTitle, selectPPTAccountAddAllShowWarningIcon, selectPPTAccountIsInCart } from '@ppt/state/entities/account/ppt-account.selectors';
import { selectPPTParcel, selectPPTParcelAddressForLookupResult, selectPPTParcelHasAddressForLookupResult } from '@ppt/state/entities/parcel/ppt-parcel.selectors';
import { NullLists } from '@shared/services/null-lists.service';
import { SimpleErrorHandling } from '@shared/services/simple-error-handling.service';
import { Viewport } from '@shared/services/viewport.service';
import { State } from '@state/model';
import { SubscriptionsService } from '@subscriptions/subscriptions.service';
import { WatchListsDialogs } from '@watch-lists/services/watch-lists-dialogs.service';
import { EgovAPIError, PPTAccount, PPTLookupNetworkService, PPTParcel, SortInfo } from 'egov-api';
import { BehaviorSubject, combineLatest, of, Subscription } from 'rxjs';
import { filter, map, switchMap, switchMapTo } from 'rxjs/operators';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
    selector: 'ppt-lookup-results',
    templateUrl: './ppt-lookup-results.component.html',
    styleUrls: ['./ppt-lookup-results.component.scss'],
    providers:
    [
        PPTLookupService,
        PPTLookupNetworkService,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParcelResultsComponent implements OnInit
{

    constructor
    (
        // Dependencies

        private readonly store: Store<State>,
        private readonly pptCartService: PPTCart,
        private readonly service: PPTLookupService,
        @Inject(forwardRef(() => AutoDraftService)) private readonly autoDraft: AutoDraftService,
        @Inject(forwardRef(() => SubscriptionsService)) private readonly subscriptions: SubscriptionsService,
        @Inject(forwardRef(() => WatchListsDialogs)) private readonly watchListsDialogs: WatchListsDialogs,
        private readonly simpleErrorHandling: SimpleErrorHandling,
        private readonly nullLists: NullLists,


        // Template dependencies

        protected readonly viewport: Viewport,
        protected readonly platform: Platform,
    )
    {
        this.loadResults = this.loadResults.bind(this);
    }


    // Init

    public ngOnInit()
    {
        this.service.loadState();
    }


    // Results

    protected static readonly RESULTS_PER_PAGE = 20;

    protected readonly nullAccounts = this.nullLists.get(ParcelResultsComponent.RESULTS_PER_PAGE);

    protected readonly results$ = new BehaviorSubject<string[] | undefined>(undefined);
    protected readonly loadingResults$ = new BehaviorSubject(false);

    private readonly accountsBeingToggled$ = new BehaviorSubject(new Set<string>());

    protected showError = false;
    protected showErrorRetry = false;

    @Output() protected readonly resultsUpdated: EventEmitter<void> = new EventEmitter<void>();

    private loadResults(): void
    {
        if (this.currentQuery)
            this.lookup();
        else
        {
            this.results$.next(undefined);
            this.resultsUpdated.emit();
        }
    }

    protected readonly resultAccounts$ = combineLatest([this.results$, this.loadingResults$]).pipe(
        switchMap(([results, loadingResults]) => results
            ? results.length
                ? combineLatest(results.map(accountNumber => this.store.select(selectPPTAccount(accountNumber)))).pipe(
                    map(accounts => loadingResults
                        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                        ? (accounts as (PPTAccount | null)[]).concat(this.nullAccounts)
                        : accounts as PPTAccount[]
                    ),
                    map(accountsOrNulls => accountsOrNulls.map(accountOrNull => accountOrNull && { ...accountOrNull }))
                )
                : of([])
            : of(this.nullAccounts),
        ),
    );

    protected readonly resultItems$ = this.resultAccounts$.pipe(
        switchMap(accounts => combineLatest(accounts.map(account => account

            // Compose Account data

            ? combineLatest([
                of(account),
                this.store.select(selectPPTAccountIsInCart(account.accountNumber)),
                this.store.select(selectPPTAccountAddAllButtonTitle(account.accountNumber)),
                this.store.select(selectPPTAccountAddAllButtonEnabled(account.accountNumber)),
                this.store.select(selectPPTAccountAddAllShowWarningIcon(account.accountNumber)),
                this.accountsBeingToggled$,
            ]).pipe(
                map(([account, inCart, cartButtonTitle, addAllEnabled, addAllWarning, accountsBeingToggled]) => ({
                    account:
                    {
                        ...account,
                        inCart,
                        cartButtonTitle,
                        addAllEnabled,
                        addAllWarning,
                        toggling: accountsBeingToggled.has(account.accountNumber)
                    },
                })),


                // Add parcel data

                switchMap(item => combineLatest([
                    of(item),
                    this.store.select(selectPPTParcel(account.parcelId!)),
                    this.store.select(selectPPTParcelHasAddressForLookupResult(account.parcelId!)),
                    this.store.select(selectPPTParcelAddressForLookupResult(account.parcelId!)),
                ])),
                map(([item, parcel, parcelHasAddress, address]) => ({
                    ...item,
                    parcel: {
                        ...parcel,
                        hasAddress: parcelHasAddress,
                        address,
                        hasZipCity:
                            !!parcel?.address?.zip
                            ||
                            !!parcel?.address?.city,
                        zipCity: this.getParcelZipCity(parcel),
                    },
                })),
            )
            : of(null)
        )))
    );

    protected removeFromResults(accountNumber: string)
    {
        if (this.results$.value)
            this.results$.next(this.results$.value.filter(id => id !== accountNumber));
        else
            throw new Error(
                'AccountResultsComponent .removeFromResults() called but this.results doesn\'t have a value.'
            );
    }

    protected resultsTrackBy(index: number, account: PPTAccount)
    {
        return account?.accountNumber || index;
    }


    // Query

    private currentQuery?: string;
    protected currentSort?: SortInfo;

    @Input() protected set queryData(data: QueryData)
    {
        if
        (
            data.query !== this.currentQuery
            ||
            data.sort !== this.currentSort?.active
            ||
            data.direction !== this.currentSort?.direction
        )
        {
            this.currentQuery = data.query;

            this.currentSort = data.sort && data.direction
                ? { active: data.sort, direction: data.direction }
                : undefined;

            setTimeout(this.loadResults, 0);
        }
    }


    // Load more

    protected loadingMore = false;
    protected ranOutOfResults = false;

    protected get showLoadMore(): boolean { return !this.ranOutOfResults; }
    protected get loadMoreDisabled(): boolean { return this.loadingResults$.value; }


    // Services state

    protected readonly pptStateLoaded$ = this.store.select(selectPPTStateLoaded);

    protected readonly accountsEnabled$ = this.store.select(selectUserAccountsEnabled);
    protected readonly autoDraftEnabled$ = this.store.select(selectPPTAutoDraftEnabled);
    protected readonly subscriptionsEnabled$ = this.store.select(selectPPTSubscriptionsEnabled);
    protected readonly watchListsEnabled$ = this.store.select(selectPPTWatchListsEnabled);


    // Lookup

    @Output() protected readonly lookupStart: EventEmitter<void> = new EventEmitter<void>();
    @Output() protected readonly lookupError: EventEmitter<void> = new EventEmitter<void>();

    private lookupSubscription?: Subscription;

    private lookup(more: boolean = false)
    {
        if (this.lookupSubscription) this.lookupSubscription.unsubscribe();

        this.showError = false;
        this.lookupStart.emit();
        this.loadingResults$.next(true);

        if (!more)
        {
            this.ranOutOfResults = false;
            this.results$.next(undefined);
        }
        else
            this.loadingMore = true;

        const next = (results: string[]) =>
        {
            if (!more)
                this.results$.next(results);
            else
            {
                this.loadingMore = false;
                this.results$.next(this.results$.value!.concat(results));
            }

            if (results.length < ParcelResultsComponent.RESULTS_PER_PAGE)
                this.ranOutOfResults = true;

            this.loadingResults$.next(false);
            this.resultsUpdated.emit();
        };

        const error = (e: EgovAPIError) =>
        {
            if (!more)
            {
                this.showError = true;
                this.showErrorRetry = !e.permanent;
            }
            else
            {
                this.loadingMore = false;
                this.simpleErrorHandling.displayBestErrorMessage(e, {
                    customMessage: 'Error loading more results.',
                    retryHandler: () => this.lookup(more)
                });
            }

            this.loadingResults$.next(false);
            this.lookupError.emit();
        };

        if (this.currentQuery)
            this.lookupSubscription = this.store.select(selectLookupServiceAvailable('property-tax')).pipe(
                filter(available => !!available),
                switchMapTo(this.service.lookup(
                    this.currentQuery,
                    this.currentSort,
                    this.results$.value?.length,
                    ParcelResultsComponent.RESULTS_PER_PAGE
                ))
            ).subscribe({ next, error });
    }

    public retry()
    {
        this.lookup();
    }

    protected loadMore()
    {
        this.lookup(true);
    }


    // Sort changed

    @Output() protected readonly sortChanged: EventEmitter<SortInfo> = new EventEmitter<SortInfo>();

    protected changeSort(newSort: SortInfo)
    {
        this.sortChanged.emit(newSort);
    }


    // Watch lists

    protected watchAccount(accountNumber: string)
    {
        const parcelId = sync(this.store.select(selectPPTAccount(accountNumber)))?.parcelId;

        if (parcelId)
        {
            const parcel = sync(this.store.select(selectPPTParcel(parcelId)));

            if (parcel)
            {
                const address = sync(this.store.select(selectPPTParcelAddressForLookupResult(parcelId)));

                this.watchListsDialogs.watchAccount(
                {
                    type: 'ppt',
                    id: accountNumber,
                    humanId: address || '',
                });
            }
        }
    }


    // Cart

    private addAccountBeingToggled(accountNumber: string)
    {
        this.accountsBeingToggled$.next(
            new Set(this.accountsBeingToggled$.value.add(accountNumber))
        );
    }

    private removeAccountBeingToggled(accountNumber: string)
    {
        const set = new Set(this.accountsBeingToggled$.value);
        set.delete(accountNumber);
        this.accountsBeingToggled$.next(set);
    }

    protected toggleAccountInCart(accountNumber: string)
    {
        if (!this.accountsBeingToggled$.value.has(accountNumber))
        {
            this.addAccountBeingToggled(accountNumber);

            this.pptCartService.toggleAccountInCart(accountNumber, true).subscribe({
                complete: () => this.removeAccountBeingToggled(accountNumber),
                error: () => this.removeAccountBeingToggled(accountNumber),
            });
        }
    }


    // Auto-draft

    protected showAutoDraftDialog(accountNumber: string)
    {
        this.autoDraft.showAutoDraftDialog(accountNumber);
    }


    // Subscriptions

    protected showSubscriptionsDialog(accountNumber: string)
    {
        this.subscriptions.showDialog(accountNumber);
    }



    // Account / Parcel data

    protected getParcelZipCity(parcel?: PPTParcel): string | undefined
    {
        const address = parcel?.address;

        if (address)
        {
            if (address.zip)
            {
                if (address.city)
                    return `${address.zip} / ${address.city}`;
                else
                    return address.zip + '';
            }
            else if (address.city)
                return address.city;
        }
    }
}

export interface QueryData
{
    query?: string;
    list?: string;
    sort: string;
    direction: 'asc' | 'desc';
}
